import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { scooterApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  Bell, 
  Plus, 
  Calendar, 
  Gauge, 
  CheckCircle,
  AlertTriangle,
  Trash2,
  Wrench,
  Clock
} from "lucide-react";

const ALERT_TYPES = {
  oil_change: { label: "Schimb ulei / Vidange", icon: "🛢️", color: "bg-amber-500" },
  tire_check: { label: "Verificare anvelope / Pneus", icon: "🔧", color: "bg-blue-500" },
  brake_check: { label: "Verificare frâne / Freins", icon: "⚠️", color: "bg-red-500" },
  battery_check: { label: "Verificare baterie / Batterie", icon: "🔋", color: "bg-green-500" },
  general_service: { label: "Service general", icon: "🔧", color: "bg-purple-500" },
  filter_change: { label: "Schimb filtre / Filtres", icon: "🌬️", color: "bg-cyan-500" },
  belt_check: { label: "Verificare curea / Courroie", icon: "⚙️", color: "bg-orange-500" },
};

export function ServiceAlerts({ scooter, onUpdate }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newAlert, setNewAlert] = useState({
    alert_type: "oil_change",
    due_date: "",
    due_mileage: 0,
    is_completed: false,
    completed_date: "",
    notes: ""
  });

  const alerts = scooter?.service_alerts || [];
  const today = new Date().toISOString().split('T')[0];

  const handleAddAlert = async () => {
    if (!newAlert.due_date && !newAlert.due_mileage) {
      toast.error("Définissez une date ou un kilométrage / Setați o dată sau kilometraj");
      return;
    }

    setLoading(true);
    try {
      const updatedAlerts = [...alerts, newAlert];
      await scooterApi.update(scooter.scooter_id, {
        service_alerts: updatedAlerts
      });
      toast.success("Alerte ajoutée / Alertă adăugată");
      setIsDialogOpen(false);
      setNewAlert({
        alert_type: "oil_change",
        due_date: "",
        due_mileage: 0,
        is_completed: false,
        completed_date: "",
        notes: ""
      });
      onUpdate?.();
    } catch (error) {
      toast.error("Erreur lors de l'ajout / Eroare la adăugare");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (index) => {
    setLoading(true);
    try {
      const updatedAlerts = [...alerts];
      updatedAlerts[index] = {
        ...updatedAlerts[index],
        is_completed: !updatedAlerts[index].is_completed,
        completed_date: !updatedAlerts[index].is_completed ? today : ""
      };
      await scooterApi.update(scooter.scooter_id, {
        service_alerts: updatedAlerts
      });
      toast.success(updatedAlerts[index].is_completed ? "Marqué comme terminé" : "Marqué comme en attente");
      onUpdate?.();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour / Eroare la actualizare");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlert = async (index) => {
    if (!window.confirm("Supprimer cette alerte? / Ștergeți această alertă?")) return;
    
    setLoading(true);
    try {
      const updatedAlerts = alerts.filter((_, i) => i !== index);
      await scooterApi.update(scooter.scooter_id, {
        service_alerts: updatedAlerts
      });
      toast.success("Alerte supprimée / Alertă ștearsă");
      onUpdate?.();
    } catch (error) {
      toast.error("Erreur lors de la suppression / Eroare la ștergere");
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (alert) => {
    if (alert.is_completed) return false;
    if (alert.due_date && alert.due_date < today) return true;
    if (alert.due_mileage && scooter?.mileage >= alert.due_mileage) return true;
    return false;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Separate pending and completed alerts
  const pendingAlerts = alerts.filter(a => !a.is_completed);
  const completedAlerts = alerts.filter(a => a.is_completed);
  const overdueCount = pendingAlerts.filter(isOverdue).length;

  return (
    <Card className="border-border" data-testid="service-alerts">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5 text-yellow-500" />
            Alertes Service / Alerte Service
            {overdueCount > 0 && (
              <Badge className="bg-red-500 text-white animate-pulse">
                {overdueCount} en retard
              </Badge>
            )}
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="btn-add-alert">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter / Adaugă
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-yellow-500" />
                  Nouvelle Alerte Service
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="alert-type">Type d'alerte / Tip alertă</Label>
                  <select
                    id="alert-type"
                    value={newAlert.alert_type}
                    onChange={(e) => setNewAlert({...newAlert, alert_type: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="select-alert-type"
                  >
                    {Object.entries(ALERT_TYPES).map(([key, { label, icon }]) => (
                      <option key={key} value={key}>{icon} {label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="due-date">Date limite / Data limită</Label>
                    <Input
                      id="due-date"
                      type="date"
                      value={newAlert.due_date}
                      onChange={(e) => setNewAlert({...newAlert, due_date: e.target.value})}
                      data-testid="input-due-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due-mileage">Km limite / Km limită</Label>
                    <Input
                      id="due-mileage"
                      type="number"
                      placeholder={`Actuel: ${scooter?.mileage || 0}`}
                      value={newAlert.due_mileage || ""}
                      onChange={(e) => setNewAlert({...newAlert, due_mileage: parseInt(e.target.value) || 0})}
                      data-testid="input-due-mileage"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="alert-notes">Notes / Note</Label>
                  <Textarea
                    id="alert-notes"
                    placeholder="Détails ou instructions..."
                    value={newAlert.notes}
                    onChange={(e) => setNewAlert({...newAlert, notes: e.target.value})}
                    rows={2}
                    data-testid="input-alert-notes"
                  />
                </div>

                <Button 
                  className="w-full" 
                  onClick={handleAddAlert}
                  disabled={loading}
                  data-testid="btn-save-alert"
                >
                  {loading ? "Se salvează..." : "Créer l'alerte / Creează alertă"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pending Alerts */}
        {pendingAlerts.length === 0 && completedAlerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune alerte de service</p>
            <p className="text-sm">Pas de alerte de service</p>
          </div>
        ) : (
          <>
            {/* Pending Alerts */}
            {pendingAlerts.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  En attente ({pendingAlerts.length})
                </h4>
                {pendingAlerts.map((alert, idx) => {
                  const originalIdx = alerts.indexOf(alert);
                  const alertType = ALERT_TYPES[alert.alert_type] || ALERT_TYPES.general_service;
                  const overdue = isOverdue(alert);
                  
                  return (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg group ${overdue ? 'bg-red-500/10 border border-red-500/30' : 'bg-secondary/50'}`}
                      data-testid={`alert-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={false}
                            onCheckedChange={() => handleToggleComplete(originalIdx)}
                            className="mt-1"
                          />
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{alertType.icon}</span>
                              <span className="font-medium">{alertType.label}</span>
                              {overdue && (
                                <Badge className="bg-red-500 text-white text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  En retard!
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                              {alert.due_date && (
                                <Badge variant="outline" className="font-mono">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDate(alert.due_date)}
                                </Badge>
                              )}
                              {alert.due_mileage > 0 && (
                                <Badge variant="outline" className="font-mono">
                                  <Gauge className="w-3 h-3 mr-1" />
                                  {alert.due_mileage.toLocaleString()} km
                                </Badge>
                              )}
                            </div>
                            {alert.notes && (
                              <p className="text-xs text-muted-foreground mt-2 italic">
                                {alert.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteAlert(originalIdx)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Completed Alerts */}
            {completedAlerts.length > 0 && (
              <div className="space-y-3 mt-4">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Terminées ({completedAlerts.length})
                </h4>
                {completedAlerts.slice(0, 3).map((alert, idx) => {
                  const originalIdx = alerts.indexOf(alert);
                  const alertType = ALERT_TYPES[alert.alert_type] || ALERT_TYPES.general_service;
                  
                  return (
                    <div 
                      key={idx} 
                      className="p-3 bg-green-500/10 rounded-lg group opacity-60"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={true}
                            onCheckedChange={() => handleToggleComplete(originalIdx)}
                            className="mt-1"
                          />
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{alertType.icon}</span>
                              <span className="font-medium line-through">{alertType.label}</span>
                              <Badge className="bg-green-500 text-white text-xs">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {formatDate(alert.completed_date)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteAlert(originalIdx)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {completedAlerts.length > 3 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{completedAlerts.length - 3} autres alertes terminées
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default ServiceAlerts;
